/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from './constants';
import { authOnlyHeaders } from './use-publish-dialog';
import {
  initialOlxDealChoice, parseOlxDeals, resolveOlxTransaction, type OlxDeal, type OlxDealType,
} from './olx-deal';

/**
 * The phone an OLX ad shows. OLX refuses an ad without one
 * (`ERROR_PHONE_REQUIRED`), so the broker picks it before publishing: the
 * responsible broker's number (default), the agency's contact, or another
 * number typed on the spot. The backend lists the first two
 * (`GET .../olx/phones`) and keeps the chosen one for edits and re-sends.
 * The same response lists the deals of a property offered for sale AND rent,
 * so the dialog can ask which one is the ad's main deal (see `olx-deal.ts`).
 */
export interface OlxPhoneOption {
  source: 'broker' | 'agency';
  /** Digits with area code (10-11). */
  phone: string;
  /** The broker's e-mail for the broker option. */
  label: string;
}

/** Select value for "type another number". */
export const OTHER_PHONE = 'other';

/**
 * A typed number as OLX digits (area code + number), or null when it is not
 * a phone OLX accepts. Mirrors the backend's `olx_phone`: the country code
 * 55 and a trunk 0 are dropped.
 */
export function olxPhoneDigits(raw: string | null | undefined): string | null {
  const digits = withoutTrunkZero(withoutCountryCode((raw ?? '').replace(/\D+/g, '')));
  return digits.length === 10 || digits.length === 11 ? digits : null;
}

/** The digits without a leading Brazilian country code 55. */
function withoutCountryCode(digits: string): string {
  return digits.length >= 12 && digits.startsWith('55') ? digits.slice(2) : digits;
}

/** The digits without the trunk 0 that precedes an area code. */
function withoutTrunkZero(digits: string): string {
  return digits.length === 11 && digits.startsWith('0') ? digits.slice(1) : digits;
}

/** The number the ad will show, from the select value and the typed number. */
export function resolveOlxPhone(choice: string, custom: string): string | null {
  return choice === OTHER_PHONE ? olxPhoneDigits(custom) : olxPhoneDigits(choice);
}

/**
 * Initial select value: the number chosen last time when it is one of the
 * options, else the first option; "other" (with the number typed in) when it
 * is not, or when there is no option at all.
 */
export function initialPhoneChoice(options: OlxPhoneOption[], selected: string | null): { choice: string; custom: string } {
  if (selected && options.some((o) => o.phone === selected)) return { choice: selected, custom: '' };
  if (selected) return { choice: OTHER_PHONE, custom: selected };
  return options.length ? { choice: options[0].phone, custom: '' } : { choice: OTHER_PHONE, custom: '' };
}

/** What `GET .../olx/phones` yields once parsed: options, initial picks and deals. */
interface LoadedPhones {
  options: OlxPhoneOption[];
  choice: string;
  custom: string;
  deals: OlxDeal[];
  dealChoice: OlxDealType | null;
}

/**
 * Parses the phones response (null when the request failed) into the options,
 * the initial phone pick, the deals and the initial main deal.
 */
function parsePhonesResponse(data: Record<string, unknown> | null | undefined): LoadedPhones {
  const body = data ?? {};
  const options: OlxPhoneOption[] = Array.isArray(body.options) ? body.options : [];
  const initial = initialPhoneChoice(options, typeof body.selected === 'string' ? body.selected : null);
  const deals = parseOlxDeals(body.transactions);
  return { options, ...initial, deals, dealChoice: initialOlxDealChoice(deals, body.transaction) };
}

/**
 * Loads the phone options and the deals while OLX is selected and tracks the
 * broker's picks.
 *
 * @param agencyId - Agency id.
 * @param propertyId - Property id.
 * @param active - Whether OLX is selected in the dialog (fetches once, on first selection).
 * @returns Options, select value/typed number with setters, the resolved phone, the deals
 * with the chosen main deal (and the `transaction` to publish), and the loading flag.
 */
export function useOlxPhone(agencyId: string, propertyId: string, active: boolean) {
  const [options, setOptions] = useState<OlxPhoneOption[] | null>(null);
  const [choice, setChoice] = useState(OTHER_PHONE);
  const [custom, setCustom] = useState('');
  const [deals, setDeals] = useState<OlxDeal[]>([]);
  const [dealChoice, setDealChoice] = useState<OlxDealType | null>(null);

  useEffect(() => {
    if (!active || options !== null) return undefined;
    let cancelled = false;
    (async () => {
      const headers = await authOnlyHeaders();
      const res = await axios
        .get(`${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/olx/phones`, { headers })
        .catch(() => null);
      if (cancelled) return;
      const loaded = parsePhonesResponse(res?.data);
      setOptions(loaded.options);
      setChoice(loaded.choice);
      setCustom(loaded.custom);
      setDeals(loaded.deals);
      setDealChoice(loaded.dealChoice);
    })();
    return () => { cancelled = true; };
  }, [active, options, agencyId, propertyId]);

  return {
    options: options ?? [], loading: active && options === null,
    choice, setChoice, custom, setCustom, phone: resolveOlxPhone(choice, custom),
    deals, dealChoice, setDealChoice, transaction: resolveOlxTransaction(deals, dealChoice),
  };
}
